// src/components/privacy/PrivacyContent.tsx
"use client";

import React from "react";
import AnimatedCard from "@/components/ui/AnimatedCard";
import { useTranslations } from "next-intl";

const SECTIONS = [
  "informationWeCollect",
  "howWeUseInfo",
  "legalBasis",
  "payments",
  "thirdPartyServices",
  "internationalTransfers",
  "cookies",
  "dataRetention",
  "yourRights",
  "childrensPrivacy",
  "dataSecurity",
  "changesToPolicy",
  "contact",
] as const;

type SectionKey = (typeof SECTIONS)[number];

export interface PrivacyContentProps {
  compact?: boolean;
}

const PrivacyContent: React.FC<PrivacyContentProps> = ({ compact = false }) => {
  const t = useTranslations("privacy");

  const getSectionId = (key: SectionKey): string => {
    return t(`sections.${key}.id`);
  };

  const renderList = (items: string[]) => (
    <ul className="list-disc ml-5 space-y-2 text-gray-300">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );

  const renderSectionContent = (key: SectionKey) => {
    switch (key) {
      case "informationWeCollect":
        return (
          <div className="space-y-6">
            <p className="text-gray-300">{t("sections.informationWeCollect.intro")}</p>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.informationWeCollect.youProvide.title")}
              </h3>
              {renderList([
                t("sections.informationWeCollect.youProvide.items.0"),
                t("sections.informationWeCollect.youProvide.items.1"),
                t("sections.informationWeCollect.youProvide.items.2"),
                t("sections.informationWeCollect.youProvide.items.3"),
              ])}
              <p className="text-gray-400 text-sm mt-2">
                {t("sections.informationWeCollect.youProvide.note")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.informationWeCollect.automatic.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.informationWeCollect.automatic.intro")}
              </p>
              {renderList([
                t("sections.informationWeCollect.automatic.items.0"),
                t("sections.informationWeCollect.automatic.items.1"),
                t("sections.informationWeCollect.automatic.items.2"),
              ])}
              <p className="text-gray-400 text-sm mt-2">
                {t("sections.informationWeCollect.automatic.note")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.informationWeCollect.fromGoogle.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.informationWeCollect.fromGoogle.intro")}
              </p>
              {renderList([
                t("sections.informationWeCollect.fromGoogle.items.0"),
                t("sections.informationWeCollect.fromGoogle.items.1"),
                t("sections.informationWeCollect.fromGoogle.items.2"),
              ])}
              <p className="text-gray-400 text-sm mt-2">
                {t("sections.informationWeCollect.fromGoogle.note")}
              </p>
            </div>
          </div>
        );

      case "howWeUseInfo":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.howWeUseInfo.intro")}</p>
            {renderList([
              t("sections.howWeUseInfo.items.0"),
              t("sections.howWeUseInfo.items.1"),
              t("sections.howWeUseInfo.items.2"),
              t("sections.howWeUseInfo.items.3"),
              t("sections.howWeUseInfo.items.4"),
            ])}
            <p className="text-gray-300 font-medium mt-4">
              {t("sections.howWeUseInfo.noSell")}
            </p>
          </div>
        );

      case "legalBasis":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.legalBasis.intro")}</p>
            {renderList([
              t("sections.legalBasis.items.0"),
              t("sections.legalBasis.items.1"),
              t("sections.legalBasis.items.2"),
            ])}
          </div>
        );

      case "payments":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.payments.content")}</p>
            <p className="text-gray-300">{t("sections.payments.noAccess")}</p>
          </div>
        );

      case "thirdPartyServices":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.thirdPartyServices.intro")}</p>
            {renderList([
              t("sections.thirdPartyServices.items.0"),
              t("sections.thirdPartyServices.items.1"),
              t("sections.thirdPartyServices.items.2"),
              t("sections.thirdPartyServices.items.3"),
            ])}
            <p className="text-gray-400 text-sm mt-2">
              {t("sections.thirdPartyServices.note")}
            </p>
          </div>
        );

      case "internationalTransfers":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.internationalTransfers.content")}</p>
            <p className="text-gray-300">{t("sections.internationalTransfers.consent")}</p>
          </div>
        );

      case "cookies":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.cookies.intro")}</p>
            {renderList([
              t("sections.cookies.items.0"),
              t("sections.cookies.items.1"),
              t("sections.cookies.items.2"),
            ])}
            <p className="text-gray-400 text-sm mt-2">
              {t("sections.cookies.note")}
            </p>
          </div>
        );

      case "dataRetention":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.dataRetention.intro")}</p>
            {renderList([
              t("sections.dataRetention.items.0"),
              t("sections.dataRetention.items.1"),
              t("sections.dataRetention.items.2"),
            ])}
            <p className="text-gray-300 mt-4">
              {t("sections.dataRetention.deletion")}
            </p>
          </div>
        );

      case "yourRights":
        return (
          <div className="space-y-6">
            <p className="text-gray-300">{t("sections.yourRights.intro")}</p>
            {renderList([
              t("sections.yourRights.items.0"),
              t("sections.yourRights.items.1"),
              t("sections.yourRights.items.2"),
              t("sections.yourRights.items.3"),
            ])}

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.yourRights.ccpa.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.yourRights.ccpa.intro")}
              </p>
              {renderList([
                t("sections.yourRights.ccpa.items.0"),
                t("sections.yourRights.ccpa.items.1"),
                t("sections.yourRights.ccpa.items.2"),
              ])}
              <p className="text-gray-300 font-medium mt-2">
                {t("sections.yourRights.ccpa.noSell")}
              </p>
            </div>
          </div>
        );

      case "childrensPrivacy":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.childrensPrivacy.content")}</p>
            <p className="text-gray-300">{t("sections.childrensPrivacy.noCollect")}</p>
          </div>
        );

      case "dataSecurity":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.dataSecurity.content")}</p>
            <p className="text-gray-400 text-sm">
              {t("sections.dataSecurity.disclaimer")}
            </p>
          </div>
        );

      case "changesToPolicy":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.changesToPolicy.content")}</p>
            <p className="text-gray-300">{t("sections.changesToPolicy.notification")}</p>
          </div>
        );

      case "contact":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.contact.content")}</p>
            <div className="bg-gray-800/30 border border-gray-700/50 p-4 rounded-lg">
              <a
                href={`mailto:${t("sections.contact.email")}`}
                className="text-purple-400 hover:text-purple-300 font-medium"
              >
                {t("sections.contact.email")}
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (compact) {
    return (
      <div className="space-y-8">
        {SECTIONS.map((key) => {
          const sectionId = getSectionId(key);
          const title = t(`sections.${key}.title`);

          return (
            <section key={key} id={sectionId}>
              <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
              {renderSectionContent(key)}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {SECTIONS.map((key, index) => {
        const sectionId = getSectionId(key);
        const title = t(`sections.${key}.title`);

        return (
          <AnimatedCard key={key} delay={0.15 + index * 0.02}>
            <section id={sectionId} className="scroll-mt-8">
              <div className="relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-white/5 backdrop-blur-xl" />
                <div className="absolute inset-[1px] rounded-xl bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

                <div className="relative p-6 md:p-8">
                  <a
                    href={`#${sectionId}`}
                    className="group flex items-center gap-2 mb-4"
                  >
                    <h2 className="text-2xl font-bold text-white group-hover:text-purple-300 transition-colors">
                      {title}
                    </h2>
                    <span className="text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      #
                    </span>
                  </a>

                  {renderSectionContent(key)}
                </div>
              </div>
            </section>
          </AnimatedCard>
        );
      })}
    </div>
  );
};

export default PrivacyContent;
